import React, { useEffect, useMemo, useState } from 'react';
import { useFormik } from 'formik';
import { Card, Container, FormFeedback, Label } from 'reactstrap';
import { toast } from 'react-toastify';

import AppInputNumberValidation from 'components/App/InputNumberValidation';
import AppSelectValidation from 'components/App/SelectValidation';
import AppLoading from 'components/App/Loading';
import Breadcrumb from 'components/App/Breadcrumb';
import Button from 'components/App/Button';
import Meta from 'components/App/Meta';
import flightBookingApi from 'api/flightBookingApi';
import {
  SERVICE_FEE_TYPE,
  SERVICE_FEE_TYPE_OPTIONS,
  getLoadErrorText,
  handleFlightApiError,
  toSystemOptions,
} from 'constants/flightBooking';

import flightSettingValidationSchema from './flightSettingValidation';
import styles from './ManagerFlightSetting.module.scss';

const PAGE_TITLE = 'Cấu hình phí vé máy bay';

const breadcrumbList = [{ name: PAGE_TITLE, isActive: true, link: '' }];

const EMPTY_SETTING = {
  serviceFee: { type: SERVICE_FEE_TYPE.FIXED, value: '' },
  perAirline: [],
};

const isPercent = (type) => type === SERVICE_FEE_TYPE.PERCENT;

function ManagerFlightSetting() {
  const [setting, setSetting] = useState(EMPTY_SETTING);
  const [systems, setSystems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    Promise.all([flightBookingApi.getFlightSetting(), flightBookingApi.getFlightSystems()])
      .then(([settingResponse, systemsResponse]) => {
        setSetting({ ...EMPTY_SETTING, ...settingResponse.payload });
        setSystems(systemsResponse.payload || []);
      })
      .catch((error) => {
        setLoadError(getLoadErrorText(error, 'cấu hình phí vé máy bay', 'Vui lòng tải lại trang.'));
        handleFlightApiError(error);
      })
      .finally(() => setIsLoading(false));
  }, []);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: setting,
    validationSchema: flightSettingValidationSchema,
    onSubmit: async (values) => {
      // PUT takes only these two keys; GET also returns updatedAt.
      const { serviceFee, perAirline } = flightSettingValidationSchema.cast(values);
      const payload = { serviceFee, perAirline };

      try {
        await flightBookingApi.updateFlightSetting(payload);
        setSetting(payload);
        toast.success('Cập nhật thành công');
      } catch (error) {
        handleFlightApiError(error);
      }
    },
  });

  const systemOptions = useMemo(() => toSystemOptions(systems), [systems]);

  const { perAirline } = validation.values;

  const onSelect = (e) => validation.setFieldValue(e.name, e.value);

  const addOverride = () => validation.setFieldValue('perAirline', [
    ...perAirline,
    { code: '', type: SERVICE_FEE_TYPE.FIXED, value: '' },
  ]);

  const removeOverride = (index) => validation.setFieldValue(
    'perAirline',
    perAirline.filter((_, i) => i !== index),
  );

  const renderFeeFields = (prefix, fee) => (
    <>
      <AppSelectValidation
        name={`${prefix}.type`}
        options={SERVICE_FEE_TYPE_OPTIONS}
        value={fee.type}
        validation={validation}
        onChange={onSelect}
        placeholder="Loại phí"
        isHiddenSearchable
      />
      <AppInputNumberValidation
        name={`${prefix}.value`}
        validation={validation}
        placeholder={isPercent(fee.type) ? 'Nhập phần trăm' : 'Nhập số tiền'}
        showCurrency={isPercent(fee.type) ? '%' : 'VND'}
        formatOptions={{ decimalScale: isPercent(fee.type) ? 2 : 0 }}
      />
    </>
  );

  return (
    <div className="page-content">
      <Meta name={PAGE_TITLE} />
      <Container fluid>
        <Breadcrumb pageTitle={PAGE_TITLE} breadcrumbList={breadcrumbList} />

        <Card className={styles.Card}>
          {isLoading && <AppLoading loading />}
          {/* An empty form here would replace the real settings on save. */}
          {loadError && <p className={styles.Note}>{loadError}</p>}
          {!isLoading && !loadError && (
            <form onSubmit={validation.handleSubmit}>
              <Label className={styles.Title}>Phí dịch vụ chung</Label>
              <p className={styles.Note}>
                Tính cho mỗi hành khách người lớn và trẻ em trên mỗi chặng. Em bé được miễn phí.
              </p>
              <div className={styles.Row}>
                {renderFeeFields('serviceFee', validation.values.serviceFee)}
              </div>

              <Label className={styles.Title}>Phí riêng theo hãng</Label>
              <p className={styles.Note}>Phí riêng thay thế phí chung cho hãng đó.</p>
              {perAirline.map((fee, index) => (
                // Rows have no id; values are fully controlled by formik.
                // eslint-disable-next-line react/no-array-index-key
                <div className={styles.Row} key={index}>
                  <AppSelectValidation
                    name={`perAirline[${index}].code`}
                    options={systemOptions}
                    value={fee.code}
                    validation={validation}
                    onChange={onSelect}
                    placeholder="Chọn hãng"
                    isHiddenSearchable
                  />
                  {renderFeeFields(`perAirline[${index}]`, fee)}
                  <Button outline onClick={() => removeOverride(index)}>Xoá</Button>
                </div>
              ))}
              {typeof validation.errors.perAirline === 'string' && (
                <FormFeedback type="invalid" className="d-block font-size-12">
                  {validation.errors.perAirline}
                </FormFeedback>
              )}
              <Button
                outline
                classes={styles.AddButton}
                disabled={perAirline.length >= systems.length}
                onClick={addOverride}
              >
                + Thêm hãng
              </Button>

              <div className={styles.Footer}>
                <Button type="submit" loading={validation.isSubmitting}>Lưu cấu hình</Button>
              </div>
            </form>
          )}
        </Card>
      </Container>
    </div>
  );
}

export default ManagerFlightSetting;
